/**
 * CTAButton Component
 *
 * Call-to-action button with attention-grabbing animations.
 * Features:
 * - Slide/pop entrance animation
 * - Continuous pulse glow effect
 * - Arrow or icon support
 * - Configurable colors and sizes
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

export interface CTAButtonProps {
  /** Button text */
  text: string;
  /** Frame when animation should start */
  startFrame?: number;
  /** Background color */
  backgroundColor?: string;
  /** Text color */
  textColor?: string;
  /** Font size */
  fontSize?: number;
  /** Padding horizontal */
  paddingX?: number;
  /** Padding vertical */
  paddingY?: number;
  /** Border radius */
  borderRadius?: number;
  /** Enable pulse glow effect */
  enablePulse?: boolean;
  /** Glow color (defaults to backgroundColor) */
  glowColor?: string;
  /** Show arrow icon */
  showArrow?: boolean;
  /** Arrow direction */
  arrowDirection?: 'right' | 'up' | 'down';
  /** Entrance animation type */
  entranceAnimation?: 'pop' | 'slideUp' | 'slideDown' | 'fadeIn';
}

export const CTAButton: React.FC<CTAButtonProps> = ({
  text,
  startFrame = 0,
  backgroundColor = '#FF6B35',
  textColor = '#ffffff',
  fontSize = 24,
  paddingX = 40,
  paddingY = 15,
  borderRadius = 50,
  enablePulse = true,
  glowColor,
  showArrow = false,
  arrowDirection = 'right',
  entranceAnimation = 'pop',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;
  const effectiveGlowColor = glowColor || backgroundColor;

  // Entrance animations
  let opacity = 1;
  let scale = 1;
  let translateY = 0;

  switch (entranceAnimation) {
    case 'pop':
      opacity = interpolate(
        relativeFrame,
        [0, fps * 0.2],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      scale = spring({
        frame: relativeFrame,
        fps,
        from: 0.3,
        to: 1,
        durationInFrames: fps * 0.5,
        config: { stiffness: 200, damping: 12 },
      });
      break;

    case 'slideUp':
      opacity = interpolate(
        relativeFrame,
        [0, fps * 0.3],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      translateY = interpolate(
        relativeFrame,
        [0, fps * 0.4],
        [30, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      break;

    case 'slideDown':
      opacity = interpolate(
        relativeFrame,
        [0, fps * 0.3],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      translateY = interpolate(
        relativeFrame,
        [0, fps * 0.4],
        [-30, 0],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      break;

    case 'fadeIn':
    default:
      opacity = interpolate(
        relativeFrame,
        [0, fps * 0.5],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      break;
  }

  // Continuous pulse glow effect
  const pulsePhase = relativeFrame * 0.1;
  const pulseIntensity = enablePulse && relativeFrame > fps * 0.5
    ? 0.5 + Math.sin(pulsePhase) * 0.3
    : 0;
  const glowSize = enablePulse
    ? 20 + Math.sin(pulsePhase) * 10
    : 0;

  // Arrow character based on direction
  const arrowChar = {
    right: '→',
    up: '↑',
    down: '↓',
  }[arrowDirection];

  // Arrow bounce animation
  const arrowOffset = showArrow
    ? Math.sin(relativeFrame * 0.2) * 3
    : 0;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        backgroundColor,
        color: textColor,
        fontSize,
        fontWeight: 700,
        fontFamily: 'Inter, sans-serif',
        padding: `${paddingY}px ${paddingX}px`,
        borderRadius,
        opacity,
        transform: `scale(${scale}) translateY(${translateY}px)`,
        boxShadow: enablePulse
          ? `0 0 ${glowSize}px rgba(${hexToRgb(effectiveGlowColor)}, ${pulseIntensity})`
          : 'none',
        transition: 'box-shadow 0.1s ease-out',
      }}
    >
      <span>{text}</span>
      {showArrow && (
        <span
          style={{
            transform: `translateX(${arrowDirection === 'right' ? arrowOffset : 0}px) translateY(${arrowDirection === 'up' ? -arrowOffset : arrowDirection === 'down' ? arrowOffset : 0}px)`,
          }}
        >
          {arrowChar}
        </span>
      )}
    </div>
  );
};

// Helper function to convert hex to RGB
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  }
  return '255, 107, 53'; // Default to orange
}
